import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, Legend, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { STATUS_META } from '../components/AgentStatusMenu';

// ms → "1.2s", "4m 30s", "2h 05m" (same convention as pages/Analytics.js)
function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return '<1s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function toLocalInput(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const TABS = ['Presence', 'Forecast', 'Schedule', 'Efficiency', 'Login Hours', 'SLA', 'Alerts'];

export default function WFM() {
  const { user } = useAuth();
  const isOwner = user?.team_role !== 'agent';
  const [tab, setTab] = useState('Presence');

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Workforce Management</h1>
      </div>
      <p className="page-desc">
        Who's working right now, how busy the days ahead look, and how the team is spending its time.
      </p>

      <div className="tabs" role="tablist">
        {TABS.map(t => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            className={'btn-tab' + (tab === t ? ' active' : '')}
            onClick={() => setTab(t)}
          >{t}</button>
        ))}
      </div>

      {tab === 'Presence' && <PresenceTab isOwner={isOwner} />}
      {tab === 'Forecast' && <ForecastTab />}
      {tab === 'Schedule' && <ScheduleTab isOwner={isOwner} />}
      {tab === 'Efficiency' && <EfficiencyTab />}
      {tab === 'Login Hours' && <LoginHoursTab />}
      {tab === 'SLA' && <SLATab />}
      {tab === 'Alerts' && <AlertsTab isOwner={isOwner} />}
    </div>
  );
}

// Per-session login/logout detail, complementing the range totals on the
// Efficiency tab with actual clock times for each connected episode —
// "Agent login hours visibility" for supervisors.
function LoginHoursTab() {
  const [from, setFrom] = useState(() => toLocalInput(new Date(new Date().setHours(0, 0, 0, 0))));
  const [to, setTo] = useState(() => toLocalInput(new Date()));
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    const fromD = new Date(from), toD = new Date(to);
    if (isNaN(fromD) || isNaN(toD) || fromD > toD) return;
    api.get('/wfm/sessions', { params: { from: fromD.toISOString(), to: toD.toISOString() } })
      .then(r => setSessions(r.data.sessions || []))
      .catch(() => setSessions([]));
  }, [from, to]);

  const summary = [];
  const byAgent = new Map();
  for (const s of sessions) {
    const key = s.agent?._id || s.agent;
    if (!byAgent.has(key)) {
      byAgent.set(key, { agent: s.agent, login_ms: 0, active_ms: 0, non_active_ms: 0 });
      summary.push(byAgent.get(key));
    }
    const totals = byAgent.get(key);
    totals.login_ms += s.duration_ms;
    totals.active_ms += s.active_ms;
    totals.non_active_ms += s.non_active_ms;
  }
  summary.sort((a, b) => b.login_ms - a.login_ms);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <label>From <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} /></label>
          <label>To <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} /></label>
        </div>

        <h3 style={{ marginTop: 0, fontSize: 14 }}>Totals for this range</h3>
        <div className="table-wrap" style={{ marginBottom: 20 }}>
          <table>
            <thead>
              <tr><th>Agent</th><th>Total hours logged in</th><th>Active</th><th>Non-active</th></tr>
            </thead>
            <tbody>
              {summary.map(a => (
                <tr key={a.agent?._id || a.agent}>
                  <td>{a.agent?.name || 'Removed member'}</td>
                  <td>{fmtDuration(a.login_ms)}</td>
                  <td>{fmtDuration(a.active_ms)}</td>
                  <td>{fmtDuration(a.non_active_ms)}</td>
                </tr>
              ))}
              {summary.length === 0 && <tr><td colSpan={4}>No sessions in this range.</td></tr>}
            </tbody>
          </table>
        </div>

        <h3 style={{ fontSize: 14 }}>Sessions</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Agent</th><th>Login</th><th>Logout</th><th>Duration</th><th>Active</th><th>Non-active</th><th>Email</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => (
                <tr key={s._id}>
                  <td>{s.agent?.name || 'Removed member'}</td>
                  <td>{new Date(s.login_at).toLocaleString()}</td>
                  <td>{s.is_open
                    ? <span style={{ color: 'var(--success)', fontWeight: 600 }}>Online now</span>
                    : new Date(s.logout_at).toLocaleString()}</td>
                  <td>{fmtDuration(s.duration_ms)}</td>
                  <td>{fmtDuration(s.active_ms)}</td>
                  <td>{fmtDuration(s.non_active_ms)}</td>
                  <td>{fmtDuration(s.email_ms)}</td>
                </tr>
              ))}
              {sessions.length === 0 && <tr><td colSpan={7}>No sessions in this range.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Offered (inbound) vs handled (agent/bot reply) traffic, bucketed by hour
// or day, as a percentage. Two measures of different scale (raw counts vs a
// percentage) get two separate charts sharing one axis each, rather than a
// dual-axis combo chart.
function SLATab() {
  const [granularity, setGranularity] = useState('hour');
  const [from, setFrom] = useState(() => toLocalInput(new Date(Date.now() - 24 * 3600 * 1000)));
  const [to, setTo] = useState(() => toLocalInput(new Date()));
  const [data, setData] = useState(null);

  useEffect(() => {
    const fromD = new Date(from), toD = new Date(to);
    if (isNaN(fromD) || isNaN(toD) || fromD > toD) return;
    api.get('/wfm/sla', { params: { from: fromD.toISOString(), to: toD.toISOString(), granularity } })
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, [from, to, granularity]);

  const series = (data?.series || []).map(s => ({
    ts: new Date(s.ts).toLocaleString(undefined, granularity === 'day'
      ? { month: 'short', day: 'numeric' }
      : { weekday: 'short', hour: '2-digit' }),
    Offered: s.offered,
    Handled: s.handled,
    handled_pct: s.handled_pct,
  }));

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label>From <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label>To <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} /></label>
        <div style={{ display: 'flex', gap: 4 }}>
          {['hour', 'day'].map(g => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              style={{
                padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)',
                background: granularity === g ? 'var(--primary)' : 'var(--surface2)',
                color: granularity === g ? '#fff' : 'var(--text)', cursor: 'pointer',
                fontWeight: 600, fontSize: 12, textTransform: 'capitalize',
              }}
            >{g}</button>
          ))}
        </div>
      </div>

      {data && (
        <>
          <p style={{ marginBottom: 16 }}>
            <strong>{data.totals.handled}</strong> of <strong>{data.totals.offered}</strong> offered messages handled
            {' '}(<strong>{data.totals.handled_pct != null ? `${data.totals.handled_pct}%` : '—'}</strong>)
          </p>

          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="ts" tick={{ fontSize: 10 }} interval={Math.ceil(series.length / 14)} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Offered" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Handled" fill="#22c55e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          <h3 style={{ fontSize: 14, marginTop: 24, marginBottom: 8 }}>Handled %</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="ts" tick={{ fontSize: 10 }} interval={Math.ceil(series.length / 14)} />
              <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
              <Tooltip />
              <Line type="monotone" dataKey="handled_pct" stroke="#6366f1" dot={false} strokeWidth={2} connectNulls />
            </LineChart>
          </ResponsiveContainer>

          <div className="table-wrap" style={{ marginTop: 24 }}>
            <table>
              <thead><tr><th>Period</th><th>Offered</th><th>Handled</th><th>Handled %</th></tr></thead>
              <tbody>
                {series.map((s, i) => (
                  <tr key={i}>
                    <td>{s.ts}</td>
                    <td>{s.Offered}</td>
                    <td>{s.Handled}</td>
                    <td>{s.handled_pct != null ? `${s.handled_pct}%` : '—'}</td>
                  </tr>
                ))}
                {series.length === 0 && <tr><td colSpan={4}>No data in this range.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// Each presence status maps to a semantic badge so the board reads the same
// way as every other status in the app (and doesn't rely on colour alone).
const STATUS_BADGE = {
  available: 'badge-success',
  not_ready: 'badge-warning',
  break: 'badge-info',
  email: 'badge-brand',
  offline: 'badge-neutral',
};

// Live status board plus (for owners/admins) the workspace's break and email
// allowances. The same policy drives the agent-side status menu in
// components/AgentStatusMenu.js — agents see this tab read-only.
function PresenceTab({ isOwner }) {
  const [board, setBoard] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [policy, setPolicy] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = () => api.get('/wfm/status').then(r => setBoard(r.data || [])).catch(() => setBoard([]));
    load();
    const id = setInterval(load, 20000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(id); clearInterval(tick); };
  }, []);

  useEffect(() => {
    api.get('/wfm/status/policy').then(r => {
      setPolicy(r.data.policy);
      setDefaults(r.data.defaults);
    }).catch(() => {});
  }, []);

  const setField = (field, value) => setPolicy(p => ({ ...p, [field]: value }));
  const setBreak = (i, field, value) => setPolicy(p => ({
    ...p, breaks: p.breaks.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)),
  }));

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const payload = {
        ...policy,
        // A break type added here has no key yet — derive one from its label.
        breaks: policy.breaks.map(b => ({
          ...b,
          key: b.key || String(b.label || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_'),
        })),
      };
      const { data } = await api.put('/wfm/status/policy', payload);
      setPolicy(data.policy);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save allowances');
    } finally {
      setSaving(false);
    }
  };

  const totalConfigured = policy
    ? policy.breaks.reduce((sum, b) => sum + (Number(b.minutes) || 0) * (Number(b.max_per_day) || 0), 0)
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Who's on what right now</h3>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -4, marginBottom: 12 }}>
          Agents set their own status from the sidebar. Only agents on Available are offered new chats.
        </p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Agent</th><th>Status</th><th>Reason</th><th>For</th></tr>
            </thead>
            <tbody>
              {board.map(row => (
                <tr key={row.agent._id}>
                  <td>{row.agent.name}</td>
                  <td>
                    <span className={`badge badge-dot ${STATUS_BADGE[row.status] || STATUS_BADGE.offline}`}>
                      {(STATUS_META[row.status] || STATUS_META.offline).label}
                    </span>
                  </td>
                  <td>{row.reason_label || '—'}</td>
                  <td>{row.started_at ? fmtDuration(now - new Date(row.started_at).getTime()) : '—'}</td>
                </tr>
              ))}
              {board.length === 0 && <tr><td colSpan={4}>No workspace members yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {policy && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 14 }}>Break &amp; email allowances</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -4, marginBottom: 12 }}>
            Allowances are written for a {policy.standard_day_hours}-hour working day
            {policy.prorate_by_working_hours
              ? ' and scaled to each agent\'s configured hours per day (WFM profile → max hours/day). An agent with no hours configured gets the full allowance.'
              : ' and applied to every agent as-is.'}
            {' '}Set a total to 0 for no cap. Agents are blocked from starting a break once the allowance is gone — Not Ready stays available.
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
            <label>Standard day (h)
              <input type="number" min={1} max={24} disabled={!isOwner} value={policy.standard_day_hours}
                onChange={e => setField('standard_day_hours', e.target.value)}
                style={{ width: 70, display: 'block' }} />
            </label>
            <label>Total break/day (min)
              <input type="number" min={0} max={1440} disabled={!isOwner} value={policy.daily_total_minutes}
                onChange={e => setField('daily_total_minutes', e.target.value)}
                style={{ width: 90, display: 'block' }} />
            </label>
            <label>Email/day (min)
              <input type="number" min={0} max={1440} disabled={!isOwner || !policy.email_enabled}
                value={policy.email_minutes_per_day}
                onChange={e => setField('email_minutes_per_day', e.target.value)}
                style={{ width: 90, display: 'block' }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" disabled={!isOwner} checked={!!policy.prorate_by_working_hours}
                onChange={e => setField('prorate_by_working_hours', e.target.checked)} />
              Scale by working hours
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" disabled={!isOwner} checked={!!policy.email_enabled}
                onChange={e => setField('email_enabled', e.target.checked)} />
              Offer the Email status
            </label>
          </div>

          <div className="table-wrap" style={{ marginBottom: 12 }}>
            <table>
              <thead>
                <tr><th>Break type</th><th>Minutes each</th><th>Per day</th><th>Daily total</th>{isOwner && <th />}</tr>
              </thead>
              <tbody>
                {policy.breaks.map((b, i) => (
                  <tr key={b.key || i}>
                    <td>
                      <input value={b.label} disabled={!isOwner} placeholder="e.g. Leg stretch"
                        onChange={e => setBreak(i, 'label', e.target.value)} style={{ width: 160 }} />
                    </td>
                    <td>
                      <input type="number" min={1} max={1440} value={b.minutes} disabled={!isOwner}
                        onChange={e => setBreak(i, 'minutes', e.target.value)} style={{ width: 70 }} />
                    </td>
                    <td>
                      <input type="number" min={1} max={50} value={b.max_per_day} disabled={!isOwner}
                        onChange={e => setBreak(i, 'max_per_day', e.target.value)} style={{ width: 60 }} />
                    </td>
                    <td>{(Number(b.minutes) || 0) * (Number(b.max_per_day) || 0)}m</td>
                    {isOwner && (
                      <td>
                        <button className="btn btn-sm" disabled={policy.breaks.length <= 1}
                          onClick={() => setPolicy(p => ({ ...p, breaks: p.breaks.filter((_, idx) => idx !== i) }))}>
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: 12, color: totalConfigured > policy.daily_total_minutes && policy.daily_total_minutes > 0 ? 'var(--warning)' : 'var(--text-muted)', marginTop: 0 }}>
            Break types add up to {totalConfigured}m/day against a {policy.daily_total_minutes || '∞'}
            {policy.daily_total_minutes ? 'm' : ''} total allowance
            {totalConfigured > policy.daily_total_minutes && policy.daily_total_minutes > 0
              ? ' — agents will hit the total before using every break.' : '.'}
          </p>

          {isOwner && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className={'btn btn-primary' + (saving ? ' is-loading' : '')} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save allowances'}
              </button>
              <button className="btn" onClick={() => setPolicy(p => ({
                ...p, breaks: [...p.breaks, { key: '', label: '', minutes: 10, max_per_day: 1 }],
              }))}>Add break type</button>
              {defaults && (
                <button className="btn" onClick={() => setPolicy({ ...defaults, breaks: defaults.breaks.map(b => ({ ...b })) })}>
                  Reset to defaults
                </button>
              )}
              {saved && <span className="badge badge-success" role="status">Saved</span>}
              {error && <span className="field-error" role="alert">{error}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const FLAG_LABELS = {
  break: 'Break time',
  avg_response: 'Avg response time',
  avg_first_response: 'Avg first response time',
};

// Flagging / alerts: agents currently over a break-time or response-time
// threshold. Break is checked live (current open break segment); response
// times are rolling averages over `window_hours` (see services/agentAlerts.js).
function AlertsTab({ isOwner }) {
  const [flagged, setFlagged] = useState([]);
  const [windowHours, setWindowHours] = useState(24);
  const [thresholds, setThresholds] = useState(null);
  const [form, setForm] = useState({ break_minutes: '', avg_response_minutes: '', avg_first_response_minutes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadAlerts = () => {
    api.get('/wfm/alerts', { params: { window_hours: windowHours } })
      .then(r => setFlagged(r.data.agents || []))
      .catch(() => setFlagged([]));
  };

  useEffect(() => {
    loadAlerts();
    const id = setInterval(loadAlerts, 30000); // live-ish without needing sockets
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [windowHours]);

  useEffect(() => {
    api.get('/wfm/alerts/thresholds').then(r => {
      setThresholds(r.data);
      setForm({
        break_minutes: r.data.break_minutes ?? '',
        avg_response_minutes: r.data.avg_response_minutes ?? '',
        avg_first_response_minutes: r.data.avg_first_response_minutes ?? '',
      });
    }).catch(() => {});
  }, []);

  const saveThresholds = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.put('/wfm/alerts/thresholds', form);
      setThresholds(data);
      loadAlerts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save thresholds');
    } finally {
      setSaving(false);
    }
  };

  const noThresholdsSet = thresholds && !thresholds.break_minutes && !thresholds.avg_response_minutes && !thresholds.avg_first_response_minutes;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {isOwner && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 14 }}>Alert thresholds</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -4, marginBottom: 12 }}>
            Leave a field blank to turn that alert off. Response-time alerts use a rolling {windowHours}h window; break is checked live.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label>Max break (min)
              <input type="number" min={1} placeholder="Off" value={form.break_minutes}
                onChange={e => setForm(f => ({ ...f, break_minutes: e.target.value }))}
                style={{ width: 70, display: 'block' }} />
            </label>
            <label>Avg response (min)
              <input type="number" min={1} placeholder="Off" value={form.avg_response_minutes}
                onChange={e => setForm(f => ({ ...f, avg_response_minutes: e.target.value }))}
                style={{ width: 70, display: 'block' }} />
            </label>
            <label>Avg first response (min)
              <input type="number" min={1} placeholder="Off" value={form.avg_first_response_minutes}
                onChange={e => setForm(f => ({ ...f, avg_first_response_minutes: e.target.value }))}
                style={{ width: 70, display: 'block' }} />
            </label>
            <label>Window (h)
              <input type="number" min={1} max={168} value={windowHours}
                onChange={e => setWindowHours(Math.min(Math.max(parseInt(e.target.value) || 24, 1), 168))}
                style={{ width: 60, display: 'block' }} />
            </label>
            <button className={'btn btn-primary' + (saving ? ' is-loading' : '')} onClick={saveThresholds} disabled={saving}>
              {saving ? 'Saving…' : 'Save thresholds'}
            </button>
          </div>
          {error && <p className="field-error" role="alert" style={{ marginBottom: 0 }}>{error}</p>}
        </div>
      )}

      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Flagged agents {flagged.length > 0 && `(${flagged.length})`}</h3>

        {noThresholdsSet && (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            No thresholds configured{isOwner ? ' — set one above to start flagging agents.' : '.'}
          </p>
        )}

        {!noThresholdsSet && flagged.length === 0 && (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No agents currently over a threshold.</p>
        )}

        {flagged.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Agent</th><th>Flags</th></tr>
              </thead>
              <tbody>
                {flagged.map(f => (
                  <tr key={f.agent?._id || f.agent}>
                    <td>{f.agent?.name || 'Removed member'}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {f.flags.map((fl, i) => (
                          <span key={i} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            color: 'var(--danger)', fontSize: 12, fontWeight: 600,
                          }}>
                            ⚠ {FLAG_LABELS[fl.type] || fl.type}: {fl.message}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ForecastTab() {
  const [from, setFrom] = useState(() => toLocalInput(new Date()));
  const [to, setTo] = useState(() => toLocalInput(new Date(Date.now() + 7 * 24 * 3600 * 1000)));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fromD = new Date(from), toD = new Date(to);
    if (isNaN(fromD) || isNaN(toD) || fromD > toD) return;
    setLoading(true);
    api.get('/wfm/forecast', { params: { from: fromD.toISOString(), to: toD.toISOString() } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [from, to]);

  const series = (data?.series || []).map(s => ({
    ts: new Date(s.ts).toLocaleString(undefined, { weekday: 'short', hour: '2-digit' }),
    forecast_volume: s.forecast_volume,
  }));

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <label>From <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label>To <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} /></label>
      </div>

      {loading && <p>Loading forecast…</p>}
      {!loading && data && (
        <>
          <p style={{ marginBottom: 12 }}>
            Forecasted contact volume: <strong>{data.total_forecast_volume}</strong> messages
            {' '}(baseline from the last {data.lookback_weeks} weeks)
          </p>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="ts" tick={{ fontSize: 10 }} interval={Math.ceil(series.length / 14)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="forecast_volume" stroke="#6366f1" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

function ScheduleTab({ isOwner }) {
  const [from, setFrom] = useState(() => toLocalInput(new Date()));
  const [to, setTo] = useState(() => toLocalInput(new Date(Date.now() + 7 * 24 * 3600 * 1000)));
  const [shiftLength, setShiftLength] = useState(8);
  const [capacity, setCapacity] = useState(6);
  const [shifts, setShifts] = useState([]);
  const [unmet, setUnmet] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const loadShifts = () => {
    const fromD = new Date(from), toD = new Date(to);
    if (isNaN(fromD) || isNaN(toD) || fromD > toD) return;
    api.get('/wfm/shifts', { params: { from: fromD.toISOString(), to: toD.toISOString() } })
      .then(r => setShifts(r.data))
      .catch(() => setShifts([]));
  };

  useEffect(loadShifts, [from, to]);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const fromD = new Date(from), toD = new Date(to);
      const { data } = await api.post('/wfm/schedule/generate', {
        from: fromD.toISOString(), to: toD.toISOString(),
        shift_length_hours: Number(shiftLength), handling_capacity_per_hour: Number(capacity),
      });
      setUnmet(data.unmet_demand || []);
      loadShifts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate schedule');
    } finally {
      setGenerating(false);
    }
  };

  const publish = async (id) => {
    await api.put(`/wfm/shifts/${id}`, { status: 'published' });
    loadShifts();
  };

  const remove = async (id) => {
    await api.delete(`/wfm/shifts/${id}`);
    loadShifts();
  };

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label>From <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label>To <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} /></label>
        {isOwner && (
          <>
            <label>Shift length (h) <input type="number" min={1} max={12} value={shiftLength} onChange={e => setShiftLength(e.target.value)} style={{ width: 60 }} /></label>
            <label>Capacity (msgs/hr/agent) <input type="number" min={1} value={capacity} onChange={e => setCapacity(e.target.value)} style={{ width: 60 }} /></label>
            <button className="btn btn-primary" onClick={generate} disabled={generating}>
              {generating ? 'Generating…' : 'Generate schedule'}
            </button>
          </>
        )}
      </div>

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}

      {unmet.length > 0 && (
        <div style={{ background: 'var(--surface2)', border: '1px solid var(--warning)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <strong style={{ color: 'var(--warning)' }}>⚠ Understaffed hours ({unmet.length})</strong>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Not enough eligible agents to fully cover demand in these hours — consider raising weekly hour targets or hiring.
          </p>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Agent</th><th>Start</th><th>End</th><th>Status</th>{isOwner && <th></th>}
            </tr>
          </thead>
          <tbody>
            {shifts.map(s => (
              <tr key={s._id}>
                <td>{s.agent?.name || 'Removed member'}</td>
                <td>{new Date(s.start_at).toLocaleString()}</td>
                <td>{new Date(s.end_at).toLocaleString()}</td>
                <td>{s.status}</td>
                {isOwner && (
                  <td style={{ display: 'flex', gap: 8 }}>
                    {s.status === 'suggested' && <button onClick={() => publish(s._id)}>Publish</button>}
                    <button onClick={() => remove(s._id)}>Delete</button>
                  </td>
                )}
              </tr>
            ))}
            {shifts.length === 0 && <tr><td colSpan={isOwner ? 5 : 4}>No shifts in this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EfficiencyTab() {
  const [from, setFrom] = useState(() => toLocalInput(new Date(Date.now() - 7 * 24 * 3600 * 1000)));
  const [to, setTo] = useState(() => toLocalInput(new Date()));
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    const fromD = new Date(from), toD = new Date(to);
    if (isNaN(fromD) || isNaN(toD) || fromD > toD) return;
    api.get('/wfm/efficiency', { params: { from: fromD.toISOString(), to: toD.toISOString() } })
      .then(r => setAgents(r.data.agents))
      .catch(() => setAgents([]));
  }, [from, to]);

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <label>From <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label>To <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} /></label>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
        "Available" means connected and not in break/not-ready/email — it is not the same as actively handling a chat.
      </p>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Agent</th><th>Login hours</th><th>Available</th><th>Break</th><th>Not Ready</th><th>Email</th>
              <th>Adherence</th><th>Messages</th><th>Avg response</th><th>Resolved</th>
            </tr>
          </thead>
          <tbody>
            {agents.map(a => (
              <tr key={a.agent._id}>
                <td>{a.agent.name || 'Removed member'}</td>
                <td>{fmtDuration(a.login_ms)}</td>
                <td>{fmtDuration(a.available_ms)}</td>
                <td>{fmtDuration(a.break_ms)}</td>
                <td>{fmtDuration(a.not_ready_ms)}</td>
                <td>{fmtDuration(a.email_ms)}</td>
                <td>{a.adherence_pct}%</td>
                <td>{a.messages_sent}</td>
                <td>{fmtDuration(a.avg_response_ms)}</td>
                <td>{a.resolved}</td>
              </tr>
            ))}
            {agents.length === 0 && <tr><td colSpan={10}>No data in this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
